import clsx from 'clsx';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/16/solid';
import { CheckBadgeIcon, ClockIcon, TableCellsIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { SidebarItem, SidebarLabel } from '../catalyst/sidebar';

const CURATION_CHILDREN = [
  { to: '/curation/list', label: 'Curation list', Icon: TableCellsIcon },
  { to: '/curation/leaderboard', label: 'Leaderboard', Icon: TrophyIcon },
  { to: '/curation/history', label: 'Curation history', Icon: ClockIcon },
] as const;

const STORAGE_KEY = 'sidebar:curation-open';

/**
 * Collapsible "Curation" nav group. The parent row links to /curation and a
 * chevron toggles the three child routes (list / leaderboard / history).
 * The toggle preference persists in localStorage, but the group is always
 * expanded while the active route is under /curation — derived per render, not
 * just at mount — so navigating into a child never hides the current page.
 */
export function CurationNav() {
  const location = useLocation();
  const inCuration = location.pathname.startsWith('/curation');
  const [open, setOpen] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true');
  const expanded = open || inCuration;

  const toggle = () => {
    setOpen((prev) => {
      localStorage.setItem(STORAGE_KEY, String(!prev));
      return !prev;
    });
  };

  return (
    <>
      <span className="relative flex flex-col">
        <SidebarItem
          hidesSidebarOnClick={true}
          to="/curation"
          as={Link}
          current={location.pathname === '/curation'}
          tooltip="Curation"
        >
          <CheckBadgeIcon />
          <SidebarLabel>Curation</SidebarLabel>
        </SidebarItem>
        <button
          type="button"
          onClick={toggle}
          aria-label={expanded ? 'Collapse curation' : 'Expand curation'}
          aria-expanded={expanded}
          className="absolute top-1/2 right-1 z-10 -translate-y-1/2 rounded p-1 text-zinc-500 hover:bg-zinc-950/5 hover:text-zinc-950 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-white lg:group-data-[collapsed=true]/sidebar:hidden"
        >
          <ChevronRightIcon
            className={clsx('size-4 transition-transform duration-200', expanded && 'rotate-90')}
          />
        </button>
      </span>
      <div
        className={clsx(
          'flex-col gap-0.5 border-l border-zinc-950/10 pl-1.5 dark:border-white/10 lg:group-data-[collapsed=true]/sidebar:ml-0 lg:group-data-[collapsed=true]/sidebar:flex lg:group-data-[collapsed=true]/sidebar:border-0 lg:group-data-[collapsed=true]/sidebar:pl-0',
          'ml-[1.375rem]',
          expanded ? 'flex' : 'hidden',
        )}
      >
        {CURATION_CHILDREN.map(({ to, label, Icon }) => (
          <SidebarItem
            key={to}
            hidesSidebarOnClick={true}
            to={to}
            as={Link}
            current={location.pathname.startsWith(to)}
            tooltip={label}
          >
            <Icon />
            <SidebarLabel>{label}</SidebarLabel>
          </SidebarItem>
        ))}
      </div>
    </>
  );
}
